import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import Form from "components/Form/Form"
import Asset from "models/Asset"
import { ArticleAny } from "@/types"
import useAssetSummaryTechnicalData from "hooks/useAssetSummaryTechnicalData"
import List from "components/List/List"
import toastMessages from "stores/toastMessages"
import FormField from "components/FormField/FormField"
import Input from "components/Input/Input"
import ShowFeaturesButton from "components/ShowFeaturesButton/ShowFeaturesButton"
import NoValue from "components/NoValue/NoValue"
import useAssetColor from "pages/AssetPage/hooks/useAssetColor"
import useAssetTechnicalDataFields from "../../hooks/useAssetTechnicalDataFields"

interface IAssetAlternativeArticleDescriptionModalProps {
    asset: Asset
    article: ArticleAny
    onClose: () => void
    onSave: (description: string) => Promise<void>
}

const AssetAlternativeArticleDescriptionModal: FC<IAssetAlternativeArticleDescriptionModalProps> = ({
    asset,
    article,
    onClose,
    onSave,
}) => {
    const { t } = useTranslation()
    const [description, setDescription] = useState(asset.altArticleDescription || article.description || "")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const canSave = description.toLowerCase().trim() !== article.description.toLowerCase().trim()

    const {
        color,
        initColorById,
    } = useAssetColor()

    useEffect(() => {
        if (asset?.colorId) {
            initColorById(asset.colorId)
        }
    }, [asset?.colorId, initColorById])

    const { getFields } = useAssetTechnicalDataFields({ asset, article, color })

    const technicalDataItems = useAssetSummaryTechnicalData({
        asset,
        article,
        getFields,
    })

    async function handleSubmit() {
        if (!canSave) return
        setIsSubmitting(true)
        try {
            await onSave(description)
            onClose()
        } catch (e) {
            toastMessages.showSystemError()
            setIsSubmitting(false)
        }

    }

    return (
        <ConfirmModal
            title={t('alternativeArticleDescription.editDescription')}
            onClose={onClose}
            maxWidth="708px"
            onConfirm={canSave ? handleSubmit : undefined}
            isBusy={isSubmitting}
            confirmButtonLabel={t('common.save')}>
            <Form onSubmit={handleSubmit}>
                <FormField
                    label={t('alternativeArticleDescription.alternativeArticleDescription')}
                    note={t('alternativeArticleDescription.alternativeArticleDescriptionNote')}
                    className="mb-3">
                    <Input
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        disabled={isSubmitting}
                        autoFocus={true}
                    />
                </FormField>
                <List items={[
                    {
                        label: t('asset.features'),
                        value: asset.hasFeaturesOrFreightUnits
                            ? <ShowFeaturesButton assetId={asset.id!} />
                            : <NoValue>{t('features.noFeaturesAdded')}</NoValue>
                    },
                    ...technicalDataItems
                ]} />
            </Form>
        </ConfirmModal>
    )
}

export default AssetAlternativeArticleDescriptionModal
